import React from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { PORTFOLIO_ITEMS, INFRASTRUCTURE_PARTNERS, TESTIMONIALS } from "../data/content";

const Portfolio = () => {
    return (
        <section id="work" className="relative py-24 lg:py-32 bg-graphite/30" data-testid="portfolio">
            <div className="max-w-7xl mx-auto px-5 lg:px-10">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
                    <div>
                        <div className="pill"><span className="accent-dot" />Selected work</div>
                        <h2 className="mt-5 font-display text-5xl lg:text-6xl text-ivory tracking-tight max-w-3xl">
                            Brands we're<br />
                            <span className="italic text-gold-shimmer">powering today.</span>
                        </h2>
                    </div>
                    <p className="max-w-md text-ivory/60 lg:text-right">
                        A growing portfolio of in-house ventures, NGO partners, and SME clients across India, Australia and beyond.
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-20">
                    {PORTFOLIO_ITEMS.map((item, i) => {
                        const Wrap = item.url ? "a" : "div";
                        const wrapProps = item.url
                            ? { href: item.url, target: "_blank", rel: "noopener noreferrer", "aria-label": `${item.name} - ${item.cta || "Visit"}` }
                            : {};
                        return (
                        <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                            className="relative"
                            data-testid={`portfolio-${item.id}`}
                        >
                        <Wrap
                            {...wrapProps}
                            className="block glass glass-hover rounded-2xl overflow-hidden flex flex-col group h-full"
                        >
                            {/* Logo stage - uniform obsidian gallery wall for all cards */}
                            <div className="aspect-square relative overflow-hidden bg-gradient-to-br from-[#0F0F14] via-obsidian to-[#070708]">
                                {/* In-development status badge - overlays top-right */}
                                {item.status === "in-development" && (
                                    <div className="absolute top-3 right-3 z-10">
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal/15 border border-teal/40 text-teal text-[9px] font-mono uppercase tracking-widest backdrop-blur-sm">
                                            <span className="w-1.5 h-1.5 rounded-full bg-teal animate-pulse" />
                                            In development
                                        </span>
                                    </div>
                                )}
                                {/* Inner spotlight */}
                                <div className="absolute inset-0 opacity-60 pointer-events-none"
                                    style={{
                                        background:
                                            "radial-gradient(ellipse 70% 50% at 50% 35%, rgba(212,168,87,0.10), transparent 65%)",
                                    }}
                                />
                                {/* Hairline frame */}
                                <div className="absolute inset-3 rounded-xl border border-white/[0.04] pointer-events-none" />

                                {item.tone === "light" ? (
                                    /* Mounted-print plaque for light-bg logos */
                                    <div
                                        className="absolute inset-0 flex items-center justify-center p-5 lg:p-6"
                                    >
                                        <div
                                            className="relative w-full h-full rounded-lg flex items-center justify-center p-4"
                                            style={{
                                                background:
                                                    "linear-gradient(180deg, #FAF6EC 0%, #F1ECDE 100%)",
                                                boxShadow:
                                                    "0 18px 38px -16px rgba(0,0,0,0.65), 0 2px 0 rgba(255,255,255,0.06) inset, 0 -1px 0 rgba(0,0,0,0.08) inset",
                                            }}
                                        >
                                            <img
                                                src={item.image}
                                                alt={`${item.name} logo`}
                                                className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                                                loading="lazy"
                                                draggable={false}
                                            />
                                            {/* Tiny gold corner accent */}
                                            <span className="absolute top-1.5 left-1.5 w-2 h-2 border-t border-l border-gold/40 rounded-tl" />
                                            <span className="absolute bottom-1.5 right-1.5 w-2 h-2 border-b border-r border-gold/40 rounded-br" />
                                        </div>
                                    </div>
                                ) : (
                                    /* Direct mount for dark-bg logos */
                                    <div className="absolute inset-0 flex items-center justify-center p-7 lg:p-8">
                                        <img
                                            src={item.image}
                                            alt={`${item.name} logo`}
                                            className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                                            loading="lazy"
                                            draggable={false}
                                        />
                                    </div>
                                )}

                                {/* Bottom hover-reveal gold rule */}
                                <div className="absolute left-6 right-6 bottom-3 h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-0 group-hover:opacity-80 transition-opacity duration-500" />
                            </div>

                            {/* Meta */}
                            <div className="p-4 flex-1 flex flex-col bg-graphite/40">
                                <div className="flex items-start justify-between gap-2">
                                    <h3 className="font-display text-base text-ivory leading-tight">{item.name}</h3>
                                    <span className="text-[9px] font-mono uppercase tracking-widest text-gold/70 shrink-0 mt-1 whitespace-nowrap">
                                        {item.tag}
                                    </span>
                                </div>
                                <p className="mt-2 text-[12px] text-ivory/55 leading-snug flex-1">
                                    {item.caption}
                                </p>
                                {item.url && (
                                    <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-gold/85 group-hover:gap-2.5 transition-all">
                                        {item.cta || "Visit"}
                                        <span aria-hidden="true">↗</span>
                                    </div>
                                )}
                            </div>
                        </Wrap>
                        </motion.div>
                        );
                    })}
                </div>

                {/* Infrastructure Partner stripe - separate from client/brand grid */}
                {INFRASTRUCTURE_PARTNERS.length > 0 && (
                    <div className="mb-20" data-testid="infrastructure-partners">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
                            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold/80 whitespace-nowrap">
                                Infrastructure &amp; Ground Operations
                            </span>
                            <span className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
                        </div>
                        <p className="text-center text-ivory/55 text-sm max-w-2xl mx-auto mb-8">
                            On-ground hardware, networks and IT muscle that keep our India delivery humming.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
                            {INFRASTRUCTURE_PARTNERS.map((item, i) => {
                                const Wrapper = item.url ? "a" : "div";
                                const wrapperProps = item.url
                                    ? { href: item.url, target: "_blank", rel: "noopener noreferrer", "aria-label": `${item.name} · ${item.cta || "Visit"}` }
                                    : {};
                                return (
                                <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                                    className="relative md:col-start-2"
                                    data-testid={`infra-${item.id}`}
                                >
                                <Wrapper
                                    {...wrapperProps}
                                    className="block glass glass-hover rounded-2xl overflow-hidden flex flex-col group h-full"
                                >
                                    <div className="aspect-square relative overflow-hidden bg-gradient-to-br from-[#0F0F14] via-obsidian to-[#070708]">
                                        <div className="absolute inset-0 opacity-60 pointer-events-none"
                                            style={{ background: "radial-gradient(ellipse 70% 50% at 50% 35%, rgba(212,168,87,0.10), transparent 65%)" }}
                                        />
                                        <div className="absolute inset-3 rounded-xl border border-white/[0.04] pointer-events-none" />
                                        <div className="absolute inset-0 flex items-center justify-center p-5 lg:p-6">
                                            <div
                                                className="relative w-full h-full rounded-lg flex items-center justify-center p-4"
                                                style={{
                                                    background: "linear-gradient(180deg, #FAF6EC 0%, #F1ECDE 100%)",
                                                    boxShadow: "0 18px 38px -16px rgba(0,0,0,0.65), 0 2px 0 rgba(255,255,255,0.06) inset, 0 -1px 0 rgba(0,0,0,0.08) inset",
                                                }}
                                            >
                                                <img
                                                    src={item.image}
                                                    alt={`${item.name} logo`}
                                                    className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                                                    loading="lazy"
                                                    draggable={false}
                                                />
                                                <span className="absolute top-1.5 left-1.5 w-2 h-2 border-t border-l border-gold/40 rounded-tl" />
                                                <span className="absolute bottom-1.5 right-1.5 w-2 h-2 border-b border-r border-gold/40 rounded-br" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-4 flex-1 flex flex-col bg-graphite/40">
                                        <div className="flex items-start justify-between gap-2">
                                            <h3 className="font-display text-base text-ivory leading-tight">{item.name}</h3>
                                            <span className="text-[9px] font-mono uppercase tracking-widest text-gold/70 shrink-0 mt-1 whitespace-nowrap">
                                                {item.tag}
                                            </span>
                                        </div>
                                        {item.proprietor && (
                                            <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-ivory/45">
                                                {item.proprietor}
                                            </div>
                                        )}
                                        <p className="mt-2 text-[12px] text-ivory/55 leading-snug flex-1">
                                            {item.caption}
                                        </p>
                                        {item.url && (
                                            <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-gold/85 group-hover:gap-2.5 transition-all">
                                                {item.cta || "Visit"}
                                                <span aria-hidden="true">↗</span>
                                            </div>
                                        )}
                                    </div>
                                </Wrapper>
                                </motion.div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="text-center mb-12">
                    <div className="inline-flex pill"><span className="accent-dot" />Founders trust us</div>
                    <h3 className="mt-5 font-display text-3xl lg:text-4xl text-ivory tracking-tight">
                        Real outcomes. <span className="italic text-gold-shimmer">Real numbers.</span>
                    </h3>
                </div>

                <div className="grid md:grid-cols-3 gap-6 lg:gap-7 relative">
                    {/* Decorative stack-shadows behind cards on lg+ */}
                    <div className="hidden lg:block absolute inset-0 pointer-events-none -z-0">
                        <div className="absolute left-[33.3%] right-[33.3%] top-3 bottom-3 rounded-2xl bg-gold/[0.04] blur-sm" />
                    </div>

                    {TESTIMONIALS.map((t, i) => (
                        <motion.figure
                            key={i}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                            className={`relative testimonial-card group ${i === 1 ? "md:-translate-y-2 lg:-translate-y-3" : ""}`}
                            data-testid={`testimonial-${i}`}
                        >
                            {/* Stack shadow tile underneath (offset paper) */}
                            <div className="absolute inset-0 rounded-2xl bg-graphite/60 border border-white/[0.04] translate-x-1.5 translate-y-1.5 -z-10 transition-transform duration-500 group-hover:translate-x-2 group-hover:translate-y-2" />

                            <div className="relative h-full rounded-2xl p-8 lg:p-9 flex flex-col overflow-hidden"
                                style={{
                                    background:
                                        "linear-gradient(160deg, rgba(212,168,87,0.05) 0%, rgba(20,20,26,0.6) 35%, rgba(15,15,20,0.85) 100%)",
                                    border: "1px solid rgba(212,168,87,0.18)",
                                    boxShadow: "0 20px 60px -30px rgba(212,168,87,0.18), inset 0 1px 0 rgba(255,255,255,0.04)",
                                }}
                            >
                                {/* Giant gold quotation watermark */}
                                <span
                                    aria-hidden="true"
                                    className="absolute -top-6 -right-2 font-display text-[10rem] leading-none text-gold/10 select-none pointer-events-none"
                                    style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 100, 'WONK' 1" }}
                                >
                                    "
                                </span>

                                {/* Small inline quote icon - for accessibility/legibility */}
                                <Quote className="relative w-7 h-7 text-gold mb-5" />

                                <blockquote className="relative text-ivory/90 leading-relaxed text-[15px] lg:text-base flex-1 font-light">
                                    {t.quote}
                                </blockquote>

                                <figcaption className="relative mt-7 pt-5 border-t border-gold/15 flex items-center gap-3">
                                    {/* Monogram circle */}
                                    <div
                                        className="w-10 h-10 rounded-full flex items-center justify-center font-display text-sm shrink-0"
                                        style={{
                                            background: "linear-gradient(180deg, #F2C763, #A07D2F)",
                                            color: "#0A0A0C",
                                            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4)",
                                        }}
                                        aria-hidden="true"
                                    >
                                        {t.name.split(/[\s.]+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join("")}
                                    </div>
                                    <div>
                                        <div className="font-display text-base text-ivory leading-tight">{t.name}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-ivory/45 font-mono mt-0.5">{t.role}</div>
                                    </div>
                                </figcaption>

                                {/* Bottom gold accent line */}
                                <div className="absolute left-8 right-8 bottom-3 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            </div>
                        </motion.figure>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Portfolio;
